import React, { useState, useEffect } from 'react';
import UserLayout from '../components/UserLayout';
import { apiClient } from '../api/api';
import {
  Play,
  TrendingUp,
  TrendingDown,
  Heart,
  Share2,
  ChevronRight,
  Sparkles,
  X,
  Calendar,
  Download,
  Link as LinkIcon,
  ExternalLink,
  DollarSign,
  ShoppingBag,
  // Clock,        // Removed: used only in Duration meta (no TikTok duration data)
  // MessageCircle, // Removed: used only in static creator profile card
  // Eye,           // Removed: used only in static creator profile card
  // UserCheck,     // Removed: used only in static creator profile card
} from 'lucide-react';
import Loader from '../components/Loader';
import './OrganicPage.css';
import { useAuth } from '../context/AuthContext';

const INITIAL_VIDEO_LIMIT = 12;

const OrganicPage = () => {
  const [data, setData] = useState({ videos: [], summary: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('Top Performing');
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [copyStatus, setCopyStatus] = useState('Copy Link');
  const [showAll, setShowAll] = useState(false);
  const { selectedShopId, dateRange } = useAuth();
  // OPENED (2026-10-21): video_link construction (scraped ID + handle) confirmed correct --
  // verified against a real shop, handle extraction fixed, link opened in a real browser and
  // landed on the right video. Was roland@-only since 2026-10-19 pending that verification.
  const canSeeVideoLinks = true;

  useEffect(() => {
    setShowAll(false); // reset when shop/range changes
    fetchOrganicMetrics();
  }, [selectedShopId, dateRange]);

  const fetchOrganicMetrics = async () => {
    try {
      setLoading(true);
      let url = `/organic-metrics?range=${dateRange}`;
      if (selectedShopId) {
        url += `&shop_id=${selectedShopId}`;
      }
      const response = await apiClient.get(url);
      
      const videosData = response.data.Videos || response.data.videos || [];
      const calculatedSummary = response.data.summary || calculateSummary(videosData);

      setData({
        videos: videosData,
        summary: calculatedSummary
      });
    } catch (error) {
      console.error('Error fetching organic metrics:', error);
      setError('Failed to load data. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  };

  const parseCurrency = (val) => {
    if (typeof val === 'number') return val;
    if (typeof val === 'string' && val !== '--') return parseFloat(val.replace(/[^0-9.-]+/g,""));
    return 0;
  };

  const calculateSummary = (videos) => {
      let totalGmv = 0;
      let totalViews = 0;
      let totalLikes = 0;
      
      videos.forEach(v => {
          // Check for flat keys first, then fallback to metrics (for backward compatibility if needed)
          const gmv = v.gmv !== undefined ? v.gmv : (v.metrics?.['Affiliate shoppable video GMV'] || v.metrics?.['GMV']);
          const views = v.views !== undefined ? v.views : v.metrics?.['Shoppable video impressions'];
          const likes = v.likes !== undefined ? v.likes : v.metrics?.['Shoppable video likes'];

          totalGmv += parseCurrency(gmv || 0);
          totalViews += parseInt(views || 0, 10);
          totalLikes += parseInt(likes || 0, 10);
      });

      return {
          total_gmv: totalGmv,
          gmv_growth: '0%', 
          total_views: totalViews,
          views_growth: '0%',
          engagement_rate: '0%', 
          engagement_growth: '0%', 
          conversion_rate: '0%', 
          conversion_growth: '0%' 
      };
  };

  const formatLargeNumber = (num) => {
    if (!num) return '0';
    const n = typeof num === 'string' ? parseCurrency(num) : num;
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return Number.isInteger(Number(n)) ? Number(n).toString() : Number(n).toFixed(2);
  };

  /*
  // Removed: static decorative sparklines — restore when real time-series data per metric is available
  const Sparkline = ({ color }) => (
    <svg className="sparkline-container" viewBox="0 0 100 40">
      <path
        d="M 0 35 Q 10 32, 20 36 T 40 25 T 60 28 T 80 15 T 100 20"
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );

  // Removed: static performance chart — restore when real per-creator time-series data is available
  const PerformanceChart = () => (
    <svg style={{ width: '100%', height: '100%', overflow: 'visible' }} viewBox="0 0 500 250">
      {[0, 50, 100, 150, 200, 250].map((y, i) => (
        <line key={i} x1="0" y1={y} x2="500" y2={y} stroke="#27272a" strokeWidth="1" />
      ))}
      <text x="-10" y="250" fill="#666" fontSize="10" textAnchor="end">50k</text>
      <text x="-10" y="200" fill="#666" fontSize="10" textAnchor="end">100k</text>
      <text x="-10" y="150" fill="#666" fontSize="10" textAnchor="end">150k</text>
      <text x="-10" y="100" fill="#666" fontSize="10" textAnchor="end">200k</text>
      <text x="-10" y="50" fill="#666" fontSize="10" textAnchor="end">250k</text>
      {['Day 1', 'Day 2', 'Day 3', 'Day 4', 'Day 5', 'Day 6', 'Day 7'].map((day, i) => (
        <text key={i} x={i * 80 + 20} y="270" fill="#666" fontSize="10" textAnchor="middle">{day}</text>
      ))}
      <path d="M 0 250 C 100 200, 200 150, 500 50" fill="none" stroke="#3b82f6" strokeWidth="3" strokeLinecap="round" />
      <path d="M 0 250 C 100 200, 200 150, 500 50 V 250 H 0" fill="url(#blueGradient)" opacity="0.2" />
      <defs>
        <linearGradient id="blueGradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3b82f6" />
          <stop offset="100%" stopColor="transparent" />
        </linearGradient>
      </defs>
    </svg>
  );
  */

  const handleCopyLink = async (link) => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopyStatus('Copied!');
      setTimeout(() => setCopyStatus('Copy Link'), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const handleDownload = async (url, filename) => {
    if (!url) return;
    const userConfirmed = window.confirm(
      "Direct video downloads are not available due to platform API limitations.\n\n" +
      "Would you like to open this video on the platform? You can download it there using the built-in download feature."
    );
    if (userConfirmed) {
      window.open(url, '_blank');
    }
  };

  return (
    <UserLayout>
      {error && (
        <div style={{background:'#2d1a1a',border:'1px solid #7f1d1d',color:'#fca5a5',padding:'12px 16px',borderRadius:'8px',margin:'16px 16px 0'}}>
          {error}
        </div>
      )}
      <div className="page-relative-wrapper">
        <div className="organic-page-container">
        {/* Top Insights Banner */}
        <div className={`insights-banner ${loading ? 'shimmer skeleton' : ''}`}>
          <div className="banner-content">
            <div className="insight-icon">
              <Sparkles size={24} color="#fff" />
            </div>
            <div className="banner-text">
              <div className="banner-title">
                ShopAI Video Insights
                <span className="live-badge">Live</span>
              </div>
              <div className="banner-description">
                {(() => {
                  if (!data.videos || data.videos.length === 0) {
                    return "🚀 Start posting shoppable videos to see AI-driven insights and performance gains.";
                  }

                  // Find top video by GMV
                  const topVideo = [...data.videos].sort((a, b) => {
                    const gmvA = parseCurrency(a.gmv !== undefined ? a.gmv : (a.metrics?.['Affiliate shoppable video GMV'] || a.metrics?.['GMV'] || 0));
                    const gmvB = parseCurrency(b.gmv !== undefined ? b.gmv : (b.metrics?.['Affiliate shoppable video GMV'] || b.metrics?.['GMV'] || 0));
                    return gmvB - gmvA;
                  })[0];

                  const topGmv = parseCurrency(topVideo.gmv !== undefined ? topVideo.gmv : (topVideo.metrics?.['Affiliate shoppable video GMV'] || topVideo.metrics?.['GMV'] || 0));
                  const totalGmv = data.summary?.total_gmv || 0;
                  const gmvContribution = totalGmv > 0 ? ((topGmv / totalGmv) * 100).toFixed(0) : 0;
                  
                  // Calculate average engagement
                  const avgEngagement = data.summary?.engagement_rate || '0%';
                  const viewsText = formatLargeNumber(data.summary?.total_views || 0);

                  return (
                    <>
                      🚀 Your top performing video "{topVideo.title || topVideo.description || 'Content'}" generated <span className="highlight-purple">${formatLargeNumber(topGmv)} in GMV</span>, contributing <span className="highlight-purple">{gmvContribution}%</span> of your total organic revenue. 
                      Your content has reached <span className="highlight-purple">{viewsText} viewers</span> with an average engagement rate of <span className="highlight-purple">{avgEngagement}</span> this period. 
                      <span className="highlight-green"> Organic traffic conversion is currently at {data.summary?.conversion_rate || '0%'}</span>.
                      <br />
                      ⚡ Analysis shows {data.videos.length} active videos driving results. Consider doubling down on content similar to your top performers{data.summary?.gmv_growth && data.summary.gmv_growth !== '0%' ? ` to push the ${data.summary.gmv_growth} GMV growth trend further` : ' to drive more GMV'}.
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
        {/* Tabs and Filtering — only shown when there is data */}
        {!loading && data.videos.length > 0 && (
          <div className="tabs-row">
            <div className="tabs">
              {['Top Performing', 'Top Revenue', 'Latest'].map(tab => (
                <button
                  key={tab}
                  className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
                  onClick={() => { setActiveTab(tab); setShowAll(false); }}
                >
                  {tab}
                </button>
              ))}
            </div>
            {data.videos.length > INITIAL_VIDEO_LIMIT && (
              <span
                className="view-all"
                style={{ cursor: 'pointer' }}
                onClick={() => setShowAll(prev => !prev)}
              >
                {showAll ? 'Show Less' : `View All (${data.videos.length})`} <ChevronRight size={16} style={{ transform: showAll ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />
              </span>
            )}
          </div>
        )}
         {/* Video Grid */}
         <div className="video-grid">
            {loading ? (
              // Show skeleton cards while loading
              [...Array(10)].map((_, i) => (
                <div key={i} className="video-card shimmer skeleton" style={{ height: '400px' }}></div>
              ))
            ) : data.videos.length === 0 ? (
              <div className="org-empty-state" style={{
                gridColumn: '1 / -1',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '60px 20px',
                color: 'rgba(255,255,255,0.4)',
                textAlign: 'center',
                gap: '16px'
              }}>
                <div style={{ fontSize: '48px' }}>🎬</div>
                <div className="org-empty-state-title" style={{ fontSize: '18px', fontWeight: 600, color: 'rgba(255,255,255,0.7)' }}>No shoppable video data for this period</div>
                <div style={{ fontSize: '14px', maxWidth: '420px', lineHeight: '1.6' }}>
                  Organic content tracks affiliate creators who post TikTok videos with your product links.
                  Data appears here once affiliates start posting shoppable content for this shop.
                </div>
              </div>
            ) : (
              (() => {
                const sortedVideos = [...data.videos].sort((a, b) => {
                  const getGmv = (v) => parseCurrency(v.gmv !== undefined ? v.gmv : (v.metrics?.['Affiliate shoppable video GMV'] || v.metrics?.['GMV'] || 0));
                  const getEngagement = (v) => {
                    const views = v.views !== undefined ? v.views : (v.metrics?.['Shoppable video impressions'] || 0);
                    const likes = v.likes !== undefined ? v.likes : (v.metrics?.['Shoppable video likes'] || 0);
                    const shares = v.shares !== undefined ? v.shares : 0;
                    return parseInt(views) + parseInt(likes) + parseInt(shares);
                  };
                  const getDate = (v) => {
                    if (!v.post_time) return 0;
                    // Try parsing 'Post time DD/MM/YYYY'
                    const dateStr = v.post_time.replace('Post time ', '');
                    const parts = dateStr.split('/');
                    if (parts.length === 3) {
                      return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`).getTime();
                    }
                    return 0;
                  };

                  if (activeTab === 'Top Revenue') {
                    return getGmv(b) - getGmv(a);
                  } else if (activeTab === 'Latest') {
                    return getDate(b) - getDate(a);
                  } else {
                    // Default to 'Top Performing' (Engagement)
                    return getEngagement(b) - getEngagement(a);
                  }
                });

                const visibleVideos = showAll ? sortedVideos : sortedVideos.slice(0, INITIAL_VIDEO_LIMIT);
                return visibleVideos.map((vid, idx) => {
                  // Map flat keys directly from API response
                  const views = vid.views !== undefined ? vid.views : (vid.metrics?.['Shoppable video impressions'] || 0);
                  const gmvRaw = vid.gmv !== undefined ? vid.gmv : (vid.metrics?.['Affiliate shoppable video GMV'] || vid.metrics?.['GMV']);
                  const likes = vid.likes !== undefined ? vid.likes : (vid.metrics?.['Shoppable video likes'] || 0);
                  const shares = vid.shares !== undefined ? vid.shares : 0;
                  const gmvVal = parseCurrency(gmvRaw || 0);
                  const postTime = vid.post_time ? vid.post_time.replace('Post time ', '') : 'Recently';
                 return (
                  <div 
                  key={idx} 
                  className="video-card"
                  onClick={() => setSelectedVideo(vid)}
              >
              <div className="video-thumbnail">
                <img
                    src={vid.thumbnail || 'https://via.placeholder.com/300x533/111/111'}
                    className="thumbnail-img"
                    alt="Video thumbnail"
                    referrerPolicy="no-referrer"
                />
                {/* <div className="duration-badge">{vid.duration || '0:45'}</div> */}
                <div className="play-button-overlay">
                   <div className="play-button-circle">
                     <Play size={20} color="#fff" fill="#fff" />
                   </div>
                </div>
                <div className="thumbnail-overlay">
                  <div className="views-overlay">
                    <Play size={10} color="#fff" fill="#fff" />
                    {formatLargeNumber(views)}
                  </div>
                  <div className="gmv-badge">
                    ${formatLargeNumber(gmvVal)}
                  </div>
                </div>
              </div>
              <div className="video-info">
                <div className="video-title">{vid.title || vid.description}</div>
                <div className="video-meta">
                  <div className="post-time">{postTime}</div>
                  <div className="engagement-stats">
                    <div className="stat-item">
                      <Heart size={14} /> {formatLargeNumber(likes)}
                    </div>
                    <div className="stat-item">
                      <Share2 size={14} /> {formatLargeNumber(shares)}
                    </div>
                  </div>
                </div>
                </div>
              </div>
            );
          })
         })()
        )}
      </div>
        {/* Bottom Summary Metrics — only shown when there is video data */}
        {(data?.videos?.length > 0) && <div className="summary-grid">
          {/* Total GMV */}
            <div className={`org-summary-card ${loading ? 'shimmer skeleton' : ''}`}>
                <div className="card-header">
                <div className="card-label-container">
                    <div className="dot" style={{backgroundColor: '#f97316'}} />
                    <span className="card-label">Total GMV</span>
                </div>
                <div className="dot" style={{backgroundColor: '#666'}} />
                </div>
                <div className="card-val">${formatLargeNumber(data.summary?.total_gmv)}</div>
                <div className={`growth-indicator ${data.summary?.gmv_growth?.includes('-') ? 'negative' : data.summary?.gmv_growth?.includes('+') ? 'positive' : 'neutral'}`}>
                {data.summary?.gmv_growth?.includes('-') ? <TrendingDown size={14} /> : data.summary?.gmv_growth?.includes('+') ? <TrendingUp size={14} /> : null} {data.summary?.gmv_growth || 'N/A'} <span className="growth-context">vs last period</span>
                </div>
                {/* <Sparkline color="#f97316" /> */}
            </div>
            {/* Total Views */}
            <div className={`org-summary-card ${loading ? 'shimmer skeleton' : ''}`}>
                <div className="card-header">
                <div className="card-label-container">
                    <div className="dot" style={{backgroundColor: '#3b82f6'}} />
                    <span className="card-label">Total Impressions</span>
                </div>
                <div className="dot" style={{backgroundColor: '#666'}} />
                </div>
                <div className="card-val">{formatLargeNumber(data.summary?.total_views)}</div>
                <div className={`growth-indicator ${data.summary?.views_growth?.includes('-') ? 'negative' : data.summary?.views_growth?.includes('+') ? 'positive' : 'neutral'}`}>
                {data.summary?.views_growth?.includes('-') ? <TrendingDown size={14} /> : data.summary?.views_growth?.includes('+') ? <TrendingUp size={14} /> : null} {data.summary?.views_growth || 'N/A'} <span className="growth-context">vs last period</span>
                </div>
                {/* <Sparkline color="#3b82f6" /> */}
            </div>
            {/* Engagement Rate */}
            <div className={`org-summary-card ${loading ? 'shimmer skeleton' : ''}`}>
                <div className="card-header">
                <div className="card-label-container">
                    <div className="dot" style={{backgroundColor: '#a855f7'}} />
                    <span className="card-label">Engagement Rate</span>
                </div>
                <div className="dot" style={{backgroundColor: '#666'}} />
                </div>
                <div className="card-val">{data.summary?.engagement_rate || '0%'}</div>
                <div className={`growth-indicator ${data.summary?.engagement_growth?.includes('-') ? 'negative' : data.summary?.engagement_growth?.includes('+') ? 'positive' : 'neutral'}`}>
                {data.summary?.engagement_growth?.includes('-') ? <TrendingDown size={14} /> : data.summary?.engagement_growth?.includes('+') ? <TrendingUp size={14} /> : null} {data.summary?.engagement_growth || 'N/A'} <span className="growth-context">vs last period</span>
                </div>
                {/* <Sparkline color="#a855f7" /> */}
            </div>
            {/* Conversion Rate */}
            <div className={`org-summary-card ${loading ? 'shimmer skeleton' : ''}`}>
                <div className="card-header">
                <div className="card-label-container">
                    <div className="dot" style={{backgroundColor: '#10b981'}} />
                    <span className="card-label">Conversion Rate</span>
                </div>
                <div className="dot" style={{backgroundColor: '#666'}} />
                </div>
                <div className="card-val">{data.summary?.conversion_rate || '0%'}</div>
                <div className={`growth-indicator ${data.summary?.conversion_growth?.includes('-') ? 'negative' : data.summary?.conversion_growth?.includes('+') ? 'positive' : 'neutral'}`}>
                {data.summary?.conversion_growth?.includes('-') ? <TrendingDown size={14} /> : data.summary?.conversion_growth?.includes('+') ? <TrendingUp size={14} /> : null} {data.summary?.conversion_growth || 'N/A'} <span className="growth-context">vs last period</span>
                </div>
                {/* <Sparkline color="#10b981" /> */}
            </div>
        </div>}
        {/* Video Detail Modal */}
        {selectedVideo && (
            <div className="org-modal-overlay" onClick={() => setSelectedVideo(null)}>
                <div className="org-modal-content" onClick={e => e.stopPropagation()}>
                    <button className="org-modal-close-btn" onClick={() => setSelectedVideo(null)}>
                        <X size={20} />
                    </button>
                    {/* Left Column */}
                    <div className="org-modal-left">
                        <div className="org-modal-video-preview">
                             <img src={selectedVideo.thumbnail || 'https://via.placeholder.com/300x533/111/111'} alt="" referrerPolicy="no-referrer" />
                             <div className="preview-play-btn">
                                <Play size={24} fill="#fff" color="#fff" />
                             </div>
                             {selectedVideo.duration && selectedVideo.duration !== '0:45' && (
                                 <div className="duration-badge" style={{top: 12, right: 12}}>
                                     {selectedVideo.duration}
                                 </div>
                             )}
                        </div>
                        <div className="org-modal-kpi-list">
                            <div className="org-kpi-card" style={{borderColor: '#f97316'}}>
                                <div style={{display: 'flex', alignItems: 'center'}}>
                                    <div className="kpi-icon-wrapper" style={{background: 'rgba(249, 115, 22, 0.2)'}}>
                                        <DollarSign size={20} color="#f97316" />
                                    </div>
                                    <div className="kpi-info">
                                        <div className="kpi-label">Total GMV</div>
                                        <div className="kpi-value">${formatLargeNumber(parseCurrency(selectedVideo.gmv || selectedVideo.metrics?.['Affiliate shoppable video GMV'] || selectedVideo.metrics?.['GMV'] || 0))}</div>
                                    </div>
                                </div>
                                {/* <div className="kpi-trend" style={{color: '#f97316'}}>
                                    <TrendingUp size={14} /> 342%
                                </div> */}
                            </div>
                            <div className="org-kpi-card" style={{borderColor: '#fbbf24'}}>
                                <div style={{display: 'flex', alignItems: 'center'}}>
                                    <div className="kpi-icon-wrapper" style={{background: 'rgba(251, 191, 36, 0.2)'}}>
                                        <ShoppingBag size={20} color="#fbbf24" />
                                    </div>
                                    <div className="kpi-info">
                                        <div className="kpi-label">Items Sold</div>
                                        <div className="kpi-value">
                                            {selectedVideo.items_sold ?? selectedVideo.metrics?.['Affiliate items sold'] ?? selectedVideo.metrics?.['Affiliate orders'] ?? '0'}
                                        </div>
                                    </div>
                                </div>
                                {/* <div className="kpi-trend" style={{color: '#fbbf24'}}>
                                    <TrendingUp size={14} /> 156%
                                </div> */}
                            </div>
                            <div className="org-kpi-card" style={{borderColor: '#3b82f6'}}>
                                <div style={{display: 'flex', alignItems: 'center'}}>
                                    <div className="kpi-icon-wrapper" style={{background: 'rgba(59, 130, 246, 0.2)'}}>
                                        <Heart size={20} color="#3b82f6" />
                                    </div>
                                    <div className="kpi-info">
                                        <div className="kpi-label">Total Likes</div>
                                        <div className="kpi-value">{formatLargeNumber(selectedVideo.likes || selectedVideo.metrics?.['Shoppable video likes'] || 0)}</div>
                                    </div>
                                </div>
                                {/* <div className="kpi-trend" style={{color: '#3b82f6'}}>
                                    <TrendingUp size={14} /> 89%
                                </div> */}
                            </div>
                            <div className="org-kpi-card" style={{borderColor: '#10b981'}}>
                                <div style={{display: 'flex', alignItems: 'center'}}>
                                    <div className="kpi-icon-wrapper" style={{background: 'rgba(16, 185, 129, 0.2)'}}>
                                        <TrendingUp size={20} color="#10b981" />
                                    </div>
                                    <div className="kpi-info">
                                        <div className="kpi-label">CTR</div>
                                        <div className="kpi-value">{selectedVideo.ctr || selectedVideo.metrics?.['Affiliate CTR'] || '0%'}</div>
                                    </div>
                                </div>
                                {/* <div className="kpi-trend" style={{color: '#10b981'}}>
                                    <TrendingUp size={14} /> 67%
                                </div> */}
                            </div>
                        </div>
                    </div>
                    {/* Right Column */}
                    <div className="org-modal-right">
                        <div className="org-modal-header-meta">
                            <div className="meta-item"><Calendar size={14} /> Uploaded: {selectedVideo.post_time?.replace('Post time ', '') || 'Recently'}</div>
                            {/* <div className="meta-item"><Clock size={14} /> Duration: {selectedVideo.duration || '0:45'}</div> */}
                        </div>
                        <div className="org-modal-actions">
                             {canSeeVideoLinks && (
                                <a
                                   href={selectedVideo.video_link || '#'}
                                   target="_blank"
                                   rel="noreferrer"
                                   className="org-action-btn btn-primary-tiktok"
                                   style={{textDecoration: 'none'}}
                                >
                                   <ExternalLink size={16} /> Open Video
                                </a>
                             )}
                             <button
                                className="org-action-btn btn-secondary"
                                disabled
                                title="Direct download not available — use Open Video to download from TikTok"
                                style={{ opacity: 0.4, cursor: 'not-allowed' }}
                             >
                                <Download size={16} /> Download
                             </button>
                             {canSeeVideoLinks && (
                                <button
                                   className="org-action-btn btn-secondary"
                                   onClick={() => handleCopyLink(selectedVideo.video_link || selectedVideo.video_url)}
                                >
                                   <LinkIcon size={16} /> {copyStatus}
                                </button>
                             )}
                        </div>
                        {selectedVideo.creator && (
                            <div className="org-creator-profile-card">
                                <div className="profile-header">
                                    {selectedVideo.creator_avatar && (
                                        <img
                                            src={selectedVideo.creator_avatar}
                                            className="profile-avatar"
                                            alt="Creator"
                                            referrerPolicy="no-referrer"
                                        />
                                    )}
                                    <div className="profile-info">
                                        <h3>{selectedVideo.creator}</h3>
                                        <div className="profile-handle">@{selectedVideo.creator?.replace('@', '')}</div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {/*
                        // Removed: static creator stats (hardcoded 284K followers, 9.2% engagement, 156K avg views)
                        // Restore when TikTok Creator API provides real follower/engagement data per creator
                        <div className="org-creator-profile-card">
                             <div className="profile-header">
                                <img src={selectedVideo.creator_avatar || 'https://via.placeholder.com/100'} className="profile-avatar" alt="Creator" referrerPolicy="no-referrer" />
                                <div className="profile-info">
                                    <h3>{selectedVideo.creator || '@creator'}</h3>
                                    <div className="profile-handle">@{selectedVideo.creator?.replace('@', '') || 'creator'}</div>
                                    <div className="verified-badge">Verified</div>
                                </div>
                                <div style={{marginLeft: 'auto'}}>
                                    <button className="org-action-btn btn-secondary" style={{padding: '8px', width: 'auto'}}><ExternalLink size={14} /></button>
                                </div>
                             </div>
                             <div className="profile-stats-row">
                                 <div className="profile-stat-box">
                                     <div className="stat-box-label"><UserCheck size={12} /> Followers</div>
                                     <div className="stat-box-value">
                                         284K
                                         <div className="trend-badge trend-up">↑ 12.4%</div>
                                     </div>
                                 </div>
                                 <div className="profile-stat-box">
                                     <div className="stat-box-label"><Eye size={12} /> Avg Views</div>
                                     <div className="stat-box-value">
                                         156K
                                         <div className="trend-badge trend-purple">↑ 8.7%</div>
                                     </div>
                                 </div>
                             </div>
                             <div className="engagement-bar-container">
                                 <div className="engagement-label-row">
                                     <span>Engagement Rate</span>
                                     <span style={{color: '#10b981'}}>9.2%</span>
                                 </div>
                                 <div className="progress-track">
                                     <div className="progress-fill" style={{width: '65%'}}></div>
                                 </div>
                             </div>
                        </div>

                        // Removed: static PerformanceChart in modal — restore when real per-creator time-series data available
                        <div className="org-chart-section">
                            <div className="org-chart-header">
                                <div className="org-chart-title">Performance Analytics</div>
                                <div className="org-chart-controls">
                                    <button className="org-chart-ctrl-btn active">Daily</button>
                                    <button className="org-chart-ctrl-btn">Weekly</button>
                                </div>
                            </div>
                            <div className="org-chart-container">
                                <PerformanceChart />
                            </div>
                        </div>
                        */}
                    </div>
                </div>
            </div>
        )}
      </div>
    </div>
  </UserLayout>
  );
};

export default OrganicPage;
